import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  Button,
  Text,
  useToast,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  HStack,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import SigningSteps from './SigningSteps';
import transformValidationErrors from 'helpers/transformValidationErrors';
import { useAppSelector, useAppDispatch } from 'hooks';

import {
  apiSaveCommercialForm,
  apiGetCommercialFiles,
  apiSubmitCommercialForm,
} from 'api';
import {
  setHasUnsavedChanges,
  setValidationErrors,
  clearValidationErrors,
  setWipStatus,
  setIncludeBilling,
  changeIsDataSaved,
} from 'reducers/commercialFormReducer';
import { COMMERCIAL_FILE_TYPES } from 'constants/fileTypes';
import type { APIApplicationFile } from 'types/api.types';
import { WIP_STATUS_BINDER_PENDING_UPLOAD } from 'variables/constants';
import { useCommercialAutoSigningEnabled } from 'hooks/useCommercialAutoSigningEnabled';

const AUTO_SIGNING_REPLACED_CHECKLIST_ITEMS = [
  'auto_irca_light_oap1',
  'auto_irca_light_cvs',
  'auto_irca_email_oap1',
  'auto_irca_email_cvs',
  'auto_fleet_oap1',
  'auto_fleet_cvs',
  'auto_garage_oap4',
  'auto_garage_supplement',
];

interface CommercialSubmitProps {
  isLocked?: boolean;
  canEditPremium?: boolean;
}

const CommercialSubmit = ({
  isLocked = false,
  canEditPremium = false,
}: CommercialSubmitProps) => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingJiraTicket, setIsCreatingJiraTicket] = useState(false);
  const bgColor = useColorModeValue('white', 'gray.800');

  const formData = useAppSelector((state) => state.commercialForm.data);
  const lineOfBusiness = useAppSelector(
    (state) => state.commercialForm.lineOfBusiness,
  );
  const hasUnsavedChanges = useAppSelector(
    (state) => state.commercialForm.hasUnsavedChanges,
  );
  const premium = useAppSelector((state) => state.commercialForm.data.premium);
  const premiumConfirmed = useAppSelector(
    (state) => state.commercialForm.premiumConfirmed,
  );
  const missingMandatoryFields = useAppSelector(
    (state) => state.commercialForm.missingMandatoryFields,
  );
  const payments = useAppSelector((state) => state.commercialForm.payments);

  const isCommercialAutoSigningEnabled = useCommercialAutoSigningEnabled();

  const hasMissingPaymentDetails = useMemo(() => {
    if (!isCommercialAutoSigningEnabled) return false;

    const selectedPaymentMethod = formData.billing?.payment_method;

    if (
      selectedPaymentMethod !== 'eft' &&
      selectedPaymentMethod !== 'credit_card'
    ) {
      return false;
    }

    if (!payments || payments.length === 0) return true;

    const hasPaymentEntry = payments.some(
      (payment) =>
        payment.type_name === selectedPaymentMethod &&
        payment.section_name === 'payment_method',
    );

    return !hasPaymentEntry;
  }, [
    formData.billing?.payment_method,
    payments,
    isCommercialAutoSigningEnabled,
  ]);

  const hasMissingDownPaymentDetails = useMemo(() => {
    if (!isCommercialAutoSigningEnabled) return false;

    const hasDownPayment = formData.billing?.has_down_payment;
    const downPaymentMethod = formData.billing?.down_payment_method;

    if (!hasDownPayment || downPaymentMethod !== 'credit_card') return false;

    if (!payments || payments.length === 0) return true;

    const hasDownPaymentEntry = payments.some(
      (payment) =>
        payment.type_name === 'credit_card' &&
        payment.section_name === 'down_payment',
    );

    return !hasDownPaymentEntry;
  }, [
    formData.billing?.has_down_payment,
    formData.billing?.down_payment_method,
    payments,
    isCommercialAutoSigningEnabled,
  ]);

  // Query for existing files
  const { data: files = [] } = useQuery({
    queryKey: ['commercial-files', uuid],
    queryFn: () => apiGetCommercialFiles(uuid!),
    refetchOnWindowFocus: false,
  }) as { data: APIApplicationFile[] };

  // Get required checklist items based on submission type and coverage bound method
  const getRequiredChecklistItems = useCallback((): string[] => {
    const submissionType = formData.submission_information?.submission_type;
    const coverageBoundMethod =
      formData.submission_information?.how_was_coverage_bound;

    const filterAutoSigningItems = (items: string[]) =>
      isCommercialAutoSigningEnabled
        ? items.filter(
            (item) => !AUTO_SIGNING_REPLACED_CHECKLIST_ITEMS.includes(item),
          )
        : items;

    if (lineOfBusiness === 'Property & Casualty') {
      if (submissionType === 'non_portal') {
        return [
          'pc_non_portal_quote_signed',
          'pc_non_portal_correspondence',
          'pc_non_portal_application',
          'pc_non_portal_bind_request',
          'pc_non_portal_quotes_declines',
          'pc_non_portal_abeyance',
        ];
      } else if (submissionType === 'portal_phone') {
        return [
          'pc_portal_quote_signed',
          'pc_portal_application',
          'pc_portal_correspondence',
          'pc_portal_building_valuations',
          'pc_portal_abeyance',
        ];
      }
    } else if (lineOfBusiness === 'Auto') {
      if (submissionType === 'irca') {
        if (
          coverageBoundMethod === 'applied_rating_email' ||
          coverageBoundMethod === 'applied_rating_portal'
        ) {
          return filterAutoSigningItems([
            'auto_irca_light_oap1',
            'auto_irca_light_cvs',
            'auto_irca_light_ownerships',
            'auto_irca_light_mvr',
            'auto_irca_light_dash',
            'auto_irca_light_correspondence',
            'auto_irca_light_abeyance',
            ...(coverageBoundMethod === 'applied_rating_email'
              ? ['auto_irca_light_bind_request']
              : []),
            ...(coverageBoundMethod === 'applied_rating_portal'
              ? ['auto_irca_light_portal_confirmation']
              : []),
          ]);
        } else if (coverageBoundMethod === 'email_email') {
          return filterAutoSigningItems([
            'auto_irca_email_oap1',
            'auto_irca_email_cvs',
            'auto_irca_email_ownerships',
            'auto_irca_email_mvr',
            'auto_irca_email_dash',
            'auto_irca_email_correspondence',
            'auto_irca_email_bind_request',
            'auto_irca_email_abeyance',
          ]);
        }
      } else if (submissionType === 'fleet') {
        return filterAutoSigningItems([
          'auto_fleet_oap1',
          'auto_fleet_cvs',
          'auto_fleet_ownerships',
          'auto_fleet_mvr',
          'auto_fleet_dash',
          'auto_fleet_fleet_loe',
          'auto_fleet_pac_contract',
          'auto_fleet_correspondence',
          'auto_fleet_bind_request',
          'auto_fleet_abeyance',
        ]);
      } else if (submissionType === 'garage') {
        return filterAutoSigningItems([
          'auto_garage_oap4',
          'auto_garage_supplement',
          'auto_garage_mvr',
          'auto_garage_dash',
          'auto_garage_correspondence',
          'auto_garage_bind_request',
          'auto_garage_abeyance',
        ]);
      }
    }
    return [];
  }, [
    formData.submission_information?.submission_type,
    formData.submission_information?.how_was_coverage_bound,
    lineOfBusiness,
    isCommercialAutoSigningEnabled,
  ]);

  // Check if all required checklist items are checked
  const areRequiredChecklistItemsChecked = useMemo(() => {
    const requiredItems = getRequiredChecklistItems();
    const checklistQuestions =
      formData.submission_information?.checklist_questions || {};

    return requiredItems.every((item) => checklistQuestions[item] === true);
  }, [
    formData.submission_information?.checklist_questions,
    getRequiredChecklistItems,
  ]);

  // Get unchecked required checklist items
  const getUncheckedRequiredItems = useCallback((): string[] => {
    const requiredItems = getRequiredChecklistItems();
    const checklistQuestions =
      formData.submission_information?.checklist_questions || {};

    return requiredItems.filter((item) => checklistQuestions[item] !== true);
  }, [
    formData.submission_information?.checklist_questions,
    getRequiredChecklistItems,
  ]);

  // Check if all required fields are filled
  const areRequiredFieldsFilled = useMemo(() => {
    const submissionType = formData.submission_information?.submission_type;
    const coverageBoundMethod =
      formData.submission_information?.how_was_coverage_bound;

    const coverageBoundRequired =
      submissionType === 'irca' || submissionType === 'portal_phone';
    const coverageBoundValid = !coverageBoundRequired || !!coverageBoundMethod;

    const baseFields =
      formData.insurance_company &&
      formData.effective_date &&
      formData.binder_policy_number &&
      formData.business_type &&
      formData.premium &&
      (formData.is_loa === true ||
        formData.is_loa === false ||
        formData.is_loa === 'yes' ||
        formData.is_loa === 'no') &&
      formData.client_name &&
      formData.customer_code &&
      submissionType &&
      formData.disclosure_and_attestation?.broker_name &&
      coverageBoundValid;

    // When the auto-signing flag is on, the legacy CommercialBilling section
    // is replaced by the shared MethodOfPayment component, which has its own
    // validation surface — skip the legacy billing checks entirely.
    if (isCommercialAutoSigningEnabled) {
      return (
        baseFields &&
        areRequiredChecklistItemsChecked &&
        missingMandatoryFields.length === 0
      );
    }

    // Billing validation depends on billing type
    const isAgencyBill = formData.billing.payment_method === 'agency_bill';

    const billingFields = isAgencyBill
      ? formData.billing.province &&
        formData.billing.business_code &&
        formData.billing.financing_type &&
        (formData.billing.financing_type !== 'premium_financing' ||
          formData.billing.signed_contract_attached === true)
      : formData.billing.province && formData.billing.payment_schedule;

    return baseFields && billingFields && areRequiredChecklistItemsChecked;
  }, [
    formData,
    areRequiredChecklistItemsChecked,
    isCommercialAutoSigningEnabled,
    missingMandatoryFields,
  ]);

  // Check if quote file is uploaded
  const hasQuote = useMemo(() => {
    return files.some((file) => file.file_type === COMMERCIAL_FILE_TYPES.QUOTE);
  }, [files]);

  // Check if declaration pages are uploaded
  const hasDeclaration = useMemo(() => {
    return files.some(
      (file) => file.file_type === COMMERCIAL_FILE_TYPES.DECLARATION_PAGES,
    );
  }, [files]);

  // Check if application file is uploaded
  const hasApplication = useMemo(() => {
    return files.some(
      (file) => file.file_type === COMMERCIAL_FILE_TYPES.APPLICATION,
    );
  }, [files]);

  // Check if supplements file is uploaded
  const hasSupplements = useMemo(() => {
    return files.some(
      (file) => file.file_type === COMMERCIAL_FILE_TYPES.SUPPLEMENT,
    );
  }, [files]);

  // Check if vehicle schedule file is uploaded (Fleet only)
  const hasVehicleSchedule = useMemo(() => {
    return files.some(
      (file) => file.file_type === COMMERCIAL_FILE_TYPES.VEHICLE_SCHEDULE,
    );
  }, [files]);

  // Check if driver schedule file is uploaded (Fleet only)
  const hasDriverSchedule = useMemo(() => {
    return files.some(
      (file) => file.file_type === COMMERCIAL_FILE_TYPES.DRIVER_SCHEDULE,
    );
  }, [files]);

  // Check if combined Application + CVS file is uploaded (IRCA only) — when
  // present, satisfies both the Application and Supplement requirements.
  const hasCombinedApplicationSupplement = useMemo(() => {
    return files.some(
      (file) =>
        file.file_type ===
        COMMERCIAL_FILE_TYPES.APPLICATION_SUPPLEMENT_COMBINED,
    );
  }, [files]);

  const isFleetSubmission =
    formData.submission_information?.submission_type === 'fleet';
  const isIrcaSubmission =
    formData.submission_information?.submission_type === 'irca';

  const applicationRequirementMet =
    hasApplication || (isIrcaSubmission && hasCombinedApplicationSupplement);
  const supplementRequirementMet =
    hasSupplements || (isIrcaSubmission && hasCombinedApplicationSupplement);

  // Check if premium confirmation is required and completed
  const isPremiumConfirmed = premium <= 10000 || premiumConfirmed;

  // Check if form can be submitted
  const canSubmit = useMemo(() => {
    const applicantAddress = formData.applicant_address;
    const hasApplicantAddress =
      !!applicantAddress?.address &&
      !!applicantAddress?.city &&
      !!applicantAddress?.province &&
      !!applicantAddress?.postal_code;

    const fleetScheduleRequirementsMet =
      !isFleetSubmission || (hasVehicleSchedule && hasDriverSchedule);

    const autoSigningRequirementsMet =
      !isCommercialAutoSigningEnabled ||
      (applicationRequirementMet &&
        supplementRequirementMet &&
        fleetScheduleRequirementsMet &&
        !!formData.applicant_email &&
        !!formData.expiry_date &&
        hasApplicantAddress);

    const uploadRequirementsMet =
      isCommercialAutoSigningEnabled || (hasQuote && hasDeclaration);

    return (
      areRequiredFieldsFilled &&
      uploadRequirementsMet &&
      !hasUnsavedChanges &&
      isPremiumConfirmed &&
      autoSigningRequirementsMet &&
      !hasMissingPaymentDetails &&
      !hasMissingDownPaymentDetails
    );
  }, [
    areRequiredFieldsFilled,
    hasQuote,
    hasDeclaration,
    hasUnsavedChanges,
    isPremiumConfirmed,
    isCommercialAutoSigningEnabled,
    applicationRequirementMet,
    supplementRequirementMet,
    hasVehicleSchedule,
    hasDriverSchedule,
    isFleetSubmission,
    formData.applicant_email,
    formData.applicant_address,
    formData.expiry_date,
    hasMissingPaymentDetails,
    hasMissingDownPaymentDetails,
  ]);

  // Get missing requirements message
  const getMissingRequirementsMessage = useCallback(() => {
    const missing: string[] = [];

    if (hasUnsavedChanges) {
      missing.push('Please save your changes before submitting');
    }

    if (!isPremiumConfirmed) {
      missing.push(
        'Please confirm the premium amount is correct (premium exceeds $10,000)',
      );
    }

    if (isCommercialAutoSigningEnabled) {
      if (!applicationRequirementMet) {
        missing.push(
          isIrcaSubmission
            ? 'Application file missing (or upload a Combined Application & CVS)'
            : 'Application file missing',
        );
      }

      if (!supplementRequirementMet) {
        missing.push(
          isIrcaSubmission
            ? 'Supplement file missing (or upload a Combined Application & CVS)'
            : 'Supplement file missing',
        );
      }

      if (isFleetSubmission && !hasVehicleSchedule) {
        missing.push('Vehicle Schedule file missing');
      }

      if (isFleetSubmission && !hasDriverSchedule) {
        missing.push('Driver Schedule file missing');
      }

      if (!formData.applicant_email) {
        missing.push('Applicant email is required signing');
      }

      if (!formData.expiry_date) {
        missing.push('Expiry date is required');
      }

      const missingAddressFields: string[] = [];
      if (!formData.applicant_address?.address)
        missingAddressFields.push('Address');
      if (!formData.applicant_address?.city) missingAddressFields.push('City');
      if (!formData.applicant_address?.province)
        missingAddressFields.push('Province');
      if (!formData.applicant_address?.postal_code)
        missingAddressFields.push('Postal Code');
      if (missingAddressFields.length > 0) {
        missing.push(
          `Applicant address fields missing: ${missingAddressFields.join(', ')}`,
        );
      }

      if (hasMissingPaymentDetails) {
        missing.push(
          'Missing payment method details — send the secure payment link and wait for the customer to complete payment information',
        );
      }

      if (hasMissingDownPaymentDetails) {
        missing.push(
          'Missing down payment details — send the secure payment link and wait for the customer to complete payment information',
        );
      }
    }

    if (!areRequiredFieldsFilled) {
      // Check individual fields to provide specific feedback
      const missingFields: string[] = [];
      if (!formData.insurance_company) missingFields.push('Insurance Company');
      if (!formData.effective_date) missingFields.push('Effective Date');
      if (!formData.binder_policy_number) missingFields.push('Binder Number');
      if (!formData.business_type) missingFields.push('Business Type');
      if (!formData.premium) missingFields.push('Premium');
      if (
        formData.is_loa !== true &&
        formData.is_loa !== false &&
        formData.is_loa !== 'yes' &&
        formData.is_loa !== 'no'
      ) {
        missingFields.push('Is this an LOA?');
      }
      if (!formData.client_name) missingFields.push('Client Name');
      if (!formData.customer_code) missingFields.push('Customer Code');
      if (!formData.submission_information?.submission_type)
        missingFields.push('Submission Type');
      const subType = formData.submission_information?.submission_type;
      if (
        (subType === 'irca' || subType === 'portal_phone') &&
        !formData.submission_information?.how_was_coverage_bound
      )
        missingFields.push('How was coverage bound');
      if (!formData.disclosure_and_attestation?.broker_name)
        missingFields.push('Broker Name');

      if (isCommercialAutoSigningEnabled) {
        if (!formData.effective_date_time) missingFields.push('Effective Time');
      }

      // Skip legacy billing fields when the auto-signing flag is on — those
      // fields are no longer surfaced (CommercialBilling is replaced by the
      // shared MethodOfPayment component).
      if (!isCommercialAutoSigningEnabled) {
        if (!formData.billing.province) missingFields.push('Province');

        const isAgencyBill = formData.billing.payment_method === 'agency_bill';
        if (isAgencyBill) {
          if (!formData.billing.business_code)
            missingFields.push('Business Code');
          if (!formData.billing.financing_type) missingFields.push('Financing');
          if (
            formData.billing.financing_type === 'premium_financing' &&
            !formData.billing.signed_contract_attached
          ) {
            missingFields.push('Signed contract attached in Power Broker');
          }
        } else {
          if (!formData.billing.payment_schedule)
            missingFields.push('Payment Schedule');
        }
      }

      if (missingFields.length > 0) {
        missing.push(`Missing fields: ${missingFields.join(', ')}`);
      }

      // Check for unchecked required checklist items
      const uncheckedItems = getUncheckedRequiredItems();
      if (uncheckedItems.length > 0) {
        missing.push(
          `Required checklist items not checked (${uncheckedItems.length} remaining)`,
        );
      }

      if (missingFields.length === 0 && uncheckedItems.length === 0) {
        missing.push('All required fields must be filled');
      }
    }

    if (isCommercialAutoSigningEnabled && missingMandatoryFields.length > 0) {
      const fieldNames = missingMandatoryFields
        .map((field) => field.name)
        .filter(Boolean);
      if (fieldNames.length > 0) {
        missing.push(
          `Method of Payment fields missing: ${fieldNames.join(', ')}`,
        );
      }
    }

    if (!isCommercialAutoSigningEnabled) {
      if (!hasQuote) {
        missing.push('Quote');
      }

      if (!hasDeclaration) {
        missing.push('Declaration Pages');
      }
    }

    return missing;
  }, [
    hasUnsavedChanges,
    areRequiredFieldsFilled,
    hasQuote,
    hasDeclaration,
    formData,
    getUncheckedRequiredItems,
    isCommercialAutoSigningEnabled,
    applicationRequirementMet,
    supplementRequirementMet,
    hasVehicleSchedule,
    hasDriverSchedule,
    isFleetSubmission,
    isIrcaSubmission,
    missingMandatoryFields,
    isPremiumConfirmed,
    hasMissingPaymentDetails,
    hasMissingDownPaymentDetails,
  ]);

  const handleSave = async () => {
    setIsSaving(true);
    dispatch(clearValidationErrors());

    // Ensure include_billing is always true
    dispatch(setIncludeBilling(true));

    try {
      // Get updated formData with include_billing set to true
      const dataToSave = {
        ...formData,
        billing: {
          ...formData.billing,
          include_billing: true,
        },
        premium_confirmed: premiumConfirmed,
      };
      const response = await apiSaveCommercialForm(uuid!, dataToSave);
      dispatch(setHasUnsavedChanges(false));
      dispatch(changeIsDataSaved(true));

      toast({
        title: 'Success',
        description: response.detail,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      const errorData = error?.response?.data || error;
      const hasValidationErrors =
        typeof errorData === 'object' &&
        errorData !== null &&
        Object.keys(errorData).length > 0;

      if (hasValidationErrors) {
        const transformedErrors = transformValidationErrors(errorData);
        dispatch(setValidationErrors(transformedErrors));

        toast({
          title: 'Validation Error',
          description: 'Please fix the errors in the form and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: 'Error',
          description:
            errorData?.error ||
            error?.message ||
            'Failed to save form data. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForm = async () => {
    // Prevent submission if validation fails
    if (!canSubmit) {
      toast({
        title: 'Cannot Submit',
        description: getMissingRequirementsMessage().join('. '),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setIsCreatingJiraTicket(true);

    // Ensure include_billing is always true before submitting
    dispatch(setIncludeBilling(true));

    try {
      const response = await apiSubmitCommercialForm(uuid!);
      dispatch(setWipStatus(WIP_STATUS_BINDER_PENDING_UPLOAD));

      toast({
        title: 'Success',
        description: response.detail || 'Form submitted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to submit form. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCreatingJiraTicket(false);
    }
  };

  const missingRequirements = getMissingRequirementsMessage();

  return (
    <>
      {!isLocked && (
        <Card mb="20px">
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="bold">
              Form Submission
            </Text>

            {!canSubmit && missingRequirements.length > 0 && (
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  <Text fontWeight="bold" mb={1}>
                    Please complete the following before submitting:
                  </Text>
                  <ul style={{ marginLeft: '20px' }}>
                    {missingRequirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {canSubmit && (
              <>
                <Alert status="success">
                  <AlertIcon />
                  <AlertDescription>
                    All required fields and files are complete. You can now
                    submit the form.
                  </AlertDescription>
                </Alert>

                {!isCommercialAutoSigningEnabled && (
                  <Flex justify="flex-end">
                    <Button
                      onClick={handleSubmitForm}
                      disabled={isCreatingJiraTicket}
                      isLoading={isCreatingJiraTicket}
                      loadingText="Submitting..."
                      colorScheme="brand"
                      variant="solid"
                      size="lg"
                    >
                      Submit Form
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </VStack>
        </Card>
      )}

      {isCommercialAutoSigningEnabled && canSubmit ? <SigningSteps /> : null}

      {hasUnsavedChanges && (!isLocked || canEditPremium) && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          zIndex={1000}
          maxW="500px"
        >
          <Card bg={bgColor} p={4} shadow="lg" borderWidth="1px">
            <HStack spacing={6}>
              <Text>You have unsaved changes</Text>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                isLoading={isSaving}
                loadingText="Saving..."
                colorScheme="yellow"
                variant="solid"
              >
                Save Changes
              </Button>
            </HStack>
          </Card>
        </Box>
      )}
    </>
  );
};

export default CommercialSubmit;
